import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { ARTIST_NAME, IMG, MILESTONES } from "@/data/content";
import {
  Reveal, ChapterLabel, ParallaxImage, ImageReveal, BookingSection, usePageTitle, EASE,
} from "@/components/Shared";

/* Editorial hero: portrait bleeds off-canvas, title overlaps it */
const BioHero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const lines = t.bio.title.split("\n");
  return (
    <section ref={ref} className="relative min-h-[100svh] overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        <img loading="lazy" decoding="async" src="/images/artist-statement.webp" alt={ARTIST_NAME} className="h-full w-full object-cover object-[50%_20%] opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0B0C] via-[#0B0B0C]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-transparent to-[#0B0B0C]/50" />
      </motion.div>
      <div className="relative z-10 flex min-h-[100svh] flex-col justify-end px-6 pb-20 pt-40 sm:px-12 lg:px-20">
        <Reveal y={16}>
          <p className="mb-8 flex items-center gap-4 font-sans text-[11px] font-medium uppercase tracking-[0.4em] text-gold">
            <span className="h-px w-12 bg-[#C5A059]/50" />
            {t.bio.eyebrow}
          </p>
        </Reveal>
        <h1 className="font-serif font-light leading-[0.88] tracking-tight">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-2">
              <motion.span
                className={`block text-[13vw] leading-[0.9] sm:text-[11vw] lg:text-[9vw] ${
                  i === 1 ? "text-outline lg:ml-[6vw]" : "text-[#F9F8F6]"
                }`}
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.25, delay: 0.2 + i * 0.15, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>
      </div>
    </section>
  );
};

const BiographyPage = () => {
  const { t, lf } = useLang();
  usePageTitle(`${t.nav.biography} — ${ARTIST_NAME}`);

  return (
    <div data-testid="biography-page">
      <BioHero />

      {/* Numbers */}
      <section className="border-y border-white/5 px-6 py-16 sm:px-12 lg:px-20">
        <div className="grid grid-cols-3 gap-8">
          {t.bio.numbers.map((n, i) => (
            <Reveal key={n.label} delay={i * 0.1} className={i === 1 ? "text-center" : i === 2 ? "text-right" : ""}>
              <p className="font-serif text-5xl font-light text-[#C5A059] sm:text-7xl">{n.value}</p>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{n.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Biography — asymmetric with drop cap */}
      <section className="relative px-6 py-20 sm:px-12 sm:py-28 lg:px-20">
        <span className="pointer-events-none absolute right-0 top-16 select-none font-serif text-[20vw] leading-none text-outline opacity-30">
          BIO
        </span>
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal><ChapterLabel label={t.bio.bioLabel} /></Reveal>
            <div className="sticky top-32 mt-10">
              <ImageReveal src="/images/bio-portrait.webp" alt={ARTIST_NAME} className="h-[62vh] w-full lg:h-[calc(100vh-10rem)]" />
            </div>
          </div>
          <div className="space-y-10 lg:col-span-7 lg:col-start-6 lg:mt-24">
            {t.bio.paragraphs.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <p
                  className={`font-sans leading-loose text-[#A1A1AA] ${
                    i === 0
                      ? "drop-cap text-base sm:text-lg"
                      : "text-sm sm:text-base lg:ml-16"
                  }`}
                >
                  {p}
                </p>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Statement direkt unter Biografie & Foto */}
        <Reveal className="mt-16 lg:mt-20">
          <div className="grid lg:grid-cols-12">
            <blockquote className="border-l-2 border-[#C5A059] pl-6 sm:pl-10 lg:col-span-7 lg:col-start-6">
              <p className="font-serif text-2xl font-light italic leading-snug text-[#F9F8F6] sm:text-3xl">{t.bio.quote}</p>
              <cite className="mt-5 block font-mono text-[10px] uppercase not-italic tracking-[0.35em] text-[#C5A059]">— {ARTIST_NAME}</cite>
            </blockquote>
          </div>
        </Reveal>
      </section>

      {/* Milestones */}
      <section className="px-6 pb-20 pt-8 sm:px-12 sm:pb-28 sm:pt-12 lg:px-20">
        <Reveal><ChapterLabel label={t.bio.milestonesLabel} /></Reveal>
        <div className="mt-12 max-w-5xl">
          {MILESTONES.map((m, i) => (
            <Reveal key={m.year} delay={i * 0.06}>
              <div
                data-testid={`milestone-${m.year}`}
                className="group relative grid grid-cols-[auto_1fr] items-baseline gap-8 border-b border-white/5 py-9 sm:gap-16"
              >
                <span className="font-serif text-4xl font-light text-[#C5A059] transition-transform duration-500 group-hover:translate-x-2 sm:text-6xl">
                  {m.year}
                </span>
                <p className="font-sans text-sm leading-relaxed text-[#A1A1AA] sm:text-base">{lf(m.text)}</p>
                <span className="absolute bottom-0 left-0 h-px w-0 bg-[#C5A059]/60 transition-all duration-700 group-hover:w-full" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Stage / Collabs — alternating editorial */}
      <section className="px-6 pb-28 sm:px-12 lg:px-20">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal><ChapterLabel label={t.bio.stageLabel} /></Reveal>
            <Reveal delay={0.1}>
              <p className="max-w-md font-sans text-sm leading-loose text-[#A1A1AA] sm:text-base">{t.bio.stageText}</p>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <ImageReveal src={IMG.soloStage} alt="Live on stage" className="aspect-[4/3]" />
          </div>
        </div>
        <div className="mt-20 grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <ImageReveal src={IMG.piano} alt="Collaborations" className="aspect-[4/3]" bw />
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal><ChapterLabel label={t.bio.collabLabel} /></Reveal>
            <Reveal delay={0.1}>
              <p className="max-w-md font-sans text-sm leading-loose text-[#A1A1AA] sm:text-base">{t.bio.collabText}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Artistic statement */}
      <section className="relative overflow-hidden border-t border-white/5">
        <ParallaxImage src={IMG.stageLights} alt="" className="absolute inset-0 h-full" imgClass="opacity-20" speed={50} />
        <div className="relative z-10 px-6 py-24 text-center sm:px-12 sm:py-32">
          <Reveal><ChapterLabel label={t.bio.statementLabel} /></Reveal>
          <Reveal delay={0.15}>
            <p className="mx-auto max-w-4xl font-serif text-2xl font-light italic leading-relaxed text-[#F9F8F6] sm:text-4xl">
              {t.bio.statementText}
            </p>
          </Reveal>
        </div>
      </section>

      <BookingSection />
    </div>
  );
};

export default BiographyPage;
